import { AlertTriangle, CheckCircle2, CircleHelp, Crosshair, Eye, ImageOff, Layers, XCircle } from 'lucide-react'
import { useId } from 'react'

import { ActionInfo, Button, ProductThumb } from '@krp/ui-kit'

import { thumbOf, type ProductPhotoUrl } from '../../../shared/product'
import { confidenceWord, DISAGREEMENT_LABELS, EVIDENCE_EFFECT_WORDS, FOCUS_READ_LABEL, reasonNote } from '../model/evidenceLabels'
import type { EvidenceEffect, EvidenceVm, HitVm, PhotoCheck, PhotoRecognition, SearchVerdict, VerdictActions, VerdictReason } from '../model/types'
import { highlightSegments } from '../model/variants'
import './scanVerdict.css'

import { judgeOf, judgeWords, metaOf } from './variantsText'

/** Сколько слов с этикетки показывается: самые весомые. */
const MAX_READ_WORDS = 12

/** Название товара, в котором слова-отличия выделены не только цветом: жирным и подчёркиванием (`<mark>`). */
function VariantName({ name, differences }: { name: string; differences: readonly string[] }) {
  return (
    <>
      {highlightSegments(name, differences).map((segment, index) =>
        segment.different ? (
          <mark key={index} className="platform-variant__diff-word">
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  )
}

/** Подпись про фото: проверено / помогло / не проверено (и почему) / не различает. Сервер ничего не сообщил — подписи нет. */
function PhotoCheckLine({ check }: { check: PhotoCheck | null | undefined }) {
  if (check === null || check === undefined) return null
  if (check === 'checked' || check === 'arbitrated') {
    return (
      <p className="platform-variant__photo" data-photo={check}>
        <CheckCircle2 size={16} aria-hidden="true" /> {check === 'arbitrated' ? 'Фото помогло различить варианты' : 'Фото проверено'}
      </p>
    )
  }
  if (check === 'absent') {
    return (
      <p className="platform-variant__photo" data-photo="absent">
        <ImageOff size={16} aria-hidden="true" /> Фото не проверено
        <span className="platform-scan__muted"> — фото в базе нет или оно сильно отличается</span>
      </p>
    )
  }
  if (check === 'incomplete') {
    return (
      <p className="platform-variant__photo" data-photo="incomplete">
        <ImageOff size={16} aria-hidden="true" /> Фото между вариантами не сравнивали
        <span className="platform-scan__muted"> — оно есть не у всех</span>
      </p>
    )
  }
  return (
    <p className="platform-variant__photo" data-photo="indistinct">
      <CircleHelp size={16} aria-hidden="true" /> Фото не различает варианты
    </p>
  )
}

/** Значок влияния свидетельства: у каждого своя форма и слово, цвет только дополняет. */
const EFFECT_ICONS: Readonly<Record<EvidenceEffect, typeof CheckCircle2>> = {
  for: CheckCircle2,
  against: XCircle,
  neutral: Eye,
  none: CircleHelp,
}

/** Одно свидетельство: название дорожки, влияние словом («за», «против», «не повлияло», «нет данных») и пояснение по коду. Нет данных — не минус. */
function EvidenceChip({ item }: { item: EvidenceVm }) {
  const Icon = EFFECT_ICONS[item.effect]
  return (
    <li className="platform-confidence__chip" data-present={item.present ? 'yes' : 'no'} data-effect={item.effect}>
      <Icon size={14} aria-hidden="true" />
      <strong>{item.label}</strong>
      <span>{item.note === null ? EVIDENCE_EFFECT_WORDS[item.effect] : `${EVIDENCE_EFFECT_WORDS[item.effect]} — ${item.note}`}</span>
    </li>
  )
}

/** Подпись расхождения «текст ≠ фото»: значок и слова. Кто назвал позицию — видно из свидетельств; не видно — подпись общая. */
function DisagreementLine({ hit }: { hit: HitVm }) {
  if (hit.disagreement === null || hit.disagreement === undefined) return null
  return (
    <p className="platform-confidence__disagree" data-side={hit.disagreement}>
      <AlertTriangle size={16} aria-hidden="true" /> {DISAGREEMENT_LABELS[hit.disagreement]}
    </p>
  )
}

/**
 * Итог проверки по фото (RP-FB77 П4) отдельной строкой, не в свёрнутом списке: значок стороны («за», «против», «не повлияло», «нет данных») и слова.
 * Проверки не было — строки нет.
 */
function JudgeLine({ hit }: { hit: HitVm }) {
  const judge = judgeOf(hit)
  if (judge === undefined) return null
  const Icon = EFFECT_ICONS[judge.effect]
  return (
    <p className="platform-confidence__judge" data-effect={judge.effect}>
      <Icon size={16} aria-hidden="true" /> {judge.label}: {judgeWords(judge)}
    </p>
  )
}

/**
 * Уверенность кандидата. Сервер прислал свидетельства (RP-FB65): слово («Скорее всего» / «Возможно» — по вердикту), «Уверенность N%» шагом 5
 * и раскрывающийся список «Из чего сложилось». Прежний ответ — прежняя строка «Совпадение N%».
 */
export function ConfidenceBlock({ hit, verdict }: { hit: HitVm; verdict: SearchVerdict }) {
  const evidence = hit.evidence ?? []
  if (evidence.length === 0) {
    return (
      <>
        {hit.matchPercent !== null && <p className="platform-scan__confidence">Совпадение {hit.matchPercent}%</p>}
        <DisagreementLine hit={hit} />
      </>
    )
  }
  const word = confidenceWord(verdict)
  const percent = hit.stepPercent ?? null
  return (
    <div className="platform-confidence">
      <p className="platform-scan__confidence">
        {word !== null && <span className="platform-confidence__word">{word}</span>}
        {word !== null && percent !== null && ' · '}
        {percent !== null && `Уверенность ${percent}%`}
      </p>
      <DisagreementLine hit={hit} />
      <JudgeLine hit={hit} />
      <details className="platform-confidence__why">
        <summary>
          Из чего сложилось
          <ActionInfo id="platform.scan.why" label="Из чего сложилась уверенность" />
        </summary>
        <ul aria-label="Из чего сложилось">
          {evidence.map((item) => (
            <EvidenceChip key={item.kind} item={item} />
          ))}
        </ul>
      </details>
    </div>
  )
}

/**
 * Предупреждения по кодам причин вердикта (`text-photo-disagree`, `text-only`, `similar-variants`): значок и слова. Причина, которую экран уже
 * показывает сам (`skip`), не повторяется; кодов без фразы нет — блока нет вовсе.
 */
export function ReasonNotes({ reasons, skip }: { reasons: readonly VerdictReason[] | undefined; skip?: string | undefined }) {
  const notes = (reasons ?? [])
    .filter((reason) => reason.code !== skip)
    .map(reasonNote)
    .filter((note): note is string => note !== null)
  if (notes.length === 0) return null
  return (
    <>
      {notes.map((note) => (
        <p key={note} className="platform-scan__note">
          <AlertTriangle size={16} aria-hidden="true" /> {note}
        </p>
      ))}
    </>
  )
}

/** Карточка одного варианта: название с выделенными отличиями, чипы отличий, фото, уверенность и два действия. */
function VariantCard({ hit, verdict, photoUrl, actions }: { hit: HitVm; verdict: SearchVerdict; photoUrl: ProductPhotoUrl | undefined; actions: VerdictActions | undefined }) {
  const differences = hit.twinGroup?.differences ?? []
  return (
    <li className="platform-variant" data-photo={hit.photoCheck ?? 'unknown'}>
      <div className="platform-scan__card-main">
        <ProductThumb src={thumbOf(hit.commonPhotoUrl, hit.identities, photoUrl)} name={hit.name} size={48} />
        <div className="platform-scan__card-text">
          <p className="platform-scan__name">
            <VariantName name={hit.name} differences={differences} />
          </p>
          <p className="platform-scan__meta">{metaOf(hit)}</p>
        </div>
      </div>
      {differences.length > 0 ? (
        <ul className="platform-variant__diffs" aria-label="Чем отличается">
          {differences.map((difference) => (
            <li key={difference} className="platform-variant__chip">
              {difference}
            </li>
          ))}
        </ul>
      ) : (
        <p className="platform-scan__muted">В названии нет слов, которых не было бы у соседей.</p>
      )}
      <PhotoCheckLine check={hit.photoCheck} />
      <ConfidenceBlock hit={hit} verdict={verdict} />
      {actions !== undefined && (
        <div className="platform-scan__buttons">
          <Button variant="primary" icon={<Eye size={18} aria-hidden="true" />} onClick={() => actions.openHit(hit)} actionId="platform.scan.variant-pick">
            Это он
          </Button>
          {actions.canWrite && (
            <Button icon={<CheckCircle2 size={18} aria-hidden="true" />} onClick={() => actions.confirmHit(hit)} actionId="platform.scan.confirm-weak">
              Прикрепить фото к нему
            </Button>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * «Что прочитано на этикетке»: самые весомые слова и числа крупно, с весом; прочитанные прицельно (`focus`) помечены значком и
 * словом. Строки, отброшенные как шум, не показываются. Нечего показывать (прежний сервер, текст не прочитан) — блока нет.
 */
export function LabelReadout({ recognized }: { recognized: PhotoRecognition | null }) {
  const titleId = useId()
  const words = (recognized?.lines ?? [])
    .filter((line) => line.band !== 'ignored' && line.text.trim() !== '')
    .sort((left, right) => right.weight - left.weight)
    .slice(0, MAX_READ_WORDS)
  if (words.length === 0) return null
  return (
    <section className="platform-readout" aria-labelledby={titleId}>
      <h4 id={titleId} className="platform-readout__title">
        Что прочитано на этикетке
      </h4>
      <ul className="platform-readout__words">
        {words.map((line, index) => {
          const focused = line.focus === true
          return (
            <li key={`${index}:${line.text}`} className="platform-readout__word" data-band={line.band} data-pass={focused ? 'focus' : undefined}>
              <span className="platform-readout__text">{line.text}</span>
              <span className="platform-readout__weight">вес {Math.round(line.weight * 100)}%</span>
              {focused && (
                <span className="platform-readout__pass">
                  <Crosshair size={14} aria-hidden="true" /> {FOCUS_READ_LABEL}
                </span>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}

/**
 * Вердикт «Выберите вариант» (RP-FB66): текст не различил серию товаров («Titan Fix» / «Titan Fix 2»), и система не выбирает за человека.
 * Баннер (значок и слова), затем 2–4 варианта рядом с отличиями и пометкой про фото, под ними — что прочитано на этикетке.
 */
export function VariantsSection({
  variants,
  verdict,
  reasons,
  recognized,
  photoUrl,
  actions,
  onNoneOfThese,
}: {
  variants: readonly HitVm[]
  verdict: SearchVerdict
  reasons: readonly VerdictReason[] | undefined
  recognized: PhotoRecognition | null
  photoUrl: ProductPhotoUrl | undefined
  actions: VerdictActions | undefined
  onNoneOfThese: () => void
}) {
  return (
    <section className="platform-scan" aria-labelledby="platform-scan-title" data-verdict="candidates" data-reason="variants">
      <h3 id="platform-scan-title" className="platform-scan__title">
        <Layers size={20} aria-hidden="true" /> Выберите вариант
      </h3>
      <p className="platform-scan__muted">Похожих товаров несколько, и по тексту они не различились. Сравните отличия и выберите свой.</p>
      <ReasonNotes reasons={reasons} skip="similar-variants" />
      <ul className="platform-variants" aria-label="Варианты">
        {variants.map((hit) => (
          <VariantCard key={hit.productId} hit={hit} verdict={verdict} photoUrl={photoUrl} actions={actions} />
        ))}
      </ul>
      <LabelReadout recognized={recognized} />
      <div className="platform-scan__footer">
        <Button variant="ghost" icon={<XCircle size={18} aria-hidden="true" />} onClick={onNoneOfThese} actionId="platform.scan.none-of-these">
          Ничего из этого
        </Button>
      </div>
    </section>
  )
}
